import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronDown, ChevronRight, Play, CheckCircle, 
  Clock, FileText, Video, Code, Download, Lock
} from 'lucide-react';

const NavigationSidebar = ({ 
  tutorial, 
  currentLesson, 
  onLessonSelect,
  userProgress = {}
}) => {
  const [expandedChapters, setExpandedChapters] = useState({});

  // Initialize all chapters as expanded by default
  React.useEffect(() => {
    if (tutorial?.chapters) {
      const initialExpanded = {};
      tutorial?.chapters?.forEach(chapter => {
        initialExpanded[chapter?.id] = true;
      });
      setExpandedChapters(initialExpanded);
    }
  }, [tutorial]);

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev?.[chapterId]
    }));
  };

  const getLessonIcon = (lessonType) => {
    switch (lessonType) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'interactive': return <Play className="w-4 h-4" />;
      case 'text': return <FileText className="w-4 h-4" />;
      case 'code': return <Code className="w-4 h-4" />;
      case 'download': return <Download className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const isLessonCompleted = (lessonId) => {
    return userProgress?.completedLessons?.includes?.(lessonId) || false;
  };

  const isLessonLocked = (lessonId) => {
    // Simple logic: lessons are locked if previous lesson in chapter isn't completed
    // In real app, this would be more sophisticated
    return false; // For demo, no lessons are locked
  };

  if (!tutorial) return null;

  const totalLessons = tutorial?.chapters?.reduce((total, chapter) => total + chapter?.lessons?.length, 0);
  const completedCount = userProgress?.completedLessons?.length || 0;
  const progressPercentage = (completedCount / totalLessons) * 100;

  return (
    <div className="h-full bg-card border-r border-border flex flex-col">
      {/* Tutorial Header */}
      <div className="p-6 border-b border-border">
        <h2 className="font-bold text-lg mb-2 line-clamp-2">{tutorial?.title}</h2>
        <p className="text-sm text-muted-foreground mb-4">{tutorial?.description}</p>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {completedCount} of {totalLessons} lessons completed
          </div>
        </div>
      </div>
      {/* Chapter Navigation */}
      <div className="flex-1 overflow-y-auto">
        {tutorial?.chapters?.map((chapter) => (
          <motion.div
            key={chapter?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="border-b border-border last:border-b-0"
          >
            {/* Chapter Header */}
            <button
              onClick={() => toggleChapter(chapter?.id)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">{chapter?.title}</h3>
                <div className="text-xs text-muted-foreground">
                  {chapter?.lessons?.length} lessons
                </div>
              </div>
              {expandedChapters?.[chapter?.id] ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {/* Chapter Lessons */}
            {expandedChapters?.[chapter?.id] && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-muted/20"
              >
                {chapter?.lessons?.map((lesson) => {
                  const isCurrentLesson = currentLesson?.id === lesson?.id;
                  const isCompleted = isLessonCompleted(lesson?.id);
                  const isLocked = isLessonLocked(lesson?.id);

                  return (
                    <button
                      key={lesson?.id}
                      onClick={() => !isLocked && onLessonSelect(lesson)}
                      disabled={isLocked}
                      className={`w-full px-8 py-3 text-left flex items-center space-x-3 hover:bg-muted/50 transition-colors group ${
                        isCurrentLesson ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {/* Lesson Status Icon */}
                      <div className="flex-shrink-0">
                        {isLocked ? (
                          <Lock className="w-4 h-4 text-muted-foreground" />
                        ) : isCompleted ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : isCurrentLesson ? (
                          <Play className="w-4 h-4 text-blue-500" />
                        ) : (
                          getLessonIcon(lesson?.type)
                        )}
                      </div>

                      {/* Lesson Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-medium line-clamp-2 ${
                          isCurrentLesson ? 'text-blue-700' : 'text-foreground'
                        }`}>
                          {lesson?.title}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{lesson?.duration}</span>
                          </div>
                          <span className="text-xs text-muted-foreground capitalize">
                            {lesson?.type}
                          </span>
                        </div>
                      </div>

                      {/* Current Lesson Indicator */}
                      {isCurrentLesson && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
      {/* Footer Actions */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Chapter {tutorial?.chapters?.findIndex(c => 
            c?.lessons?.some(l => l?.id === currentLesson?.id)
          ) + 1 || 1} of {tutorial?.chapters?.length}</span>
          <span>{tutorial?.estimatedTime} total</span>
        </div>
      </div>
    </div>
  );
};

export default NavigationSidebar;